import React from "react";

export const StaticPage = ({ title, kicker, children, testId }) => (
    <div className="max-w-[900px] mx-auto px-4 md:px-10 py-16" data-testid={testId}>
        <div className="font-mono text-[10px] uppercase text-neutral-500">{kicker}</div>
        <h1 className="font-display text-5xl md:text-7xl tracking-tighter mt-1">{title}</h1>
        <div className="mt-8 space-y-6 text-neutral-800 leading-relaxed">{children}</div>
    </div>
);

export const About = () => (
    <StaticPage kicker="Story" title="Clonzo is noise you wear." testId="about-page">
        <p>Clonzo is an India-based print-on-demand streetwear label. We started because we were tired of the way fashion works — mountains of unsold stock, mass sameness, and clothes designed for shelves, not people.</p>
        <p>We make each piece only after you order it. That means less waste, tighter drops, and prints that actually mean something. Whether you're on the tracks, on the block, or on a Zoom call — Clonzo dresses loud.</p>
        <p>Heavyweight cotton. Sharp graphics. Built to outlast the trend cycle.</p>
    </StaticPage>
);

export const Faqs = () => (
    <StaticPage kicker="Support" title="FAQs" testId="faqs-page">
        {[
            ["How long does shipping take?", "Since every piece is printed to order, orders typically ship in 5–7 business days across India."],
            ["What sizes do you carry?", "S, M, L, XL and XXL for most tees. Check the size chart on each product page."],
            ["Can I return an item because I don't like it?", "No — we only allow exchanges for size/preference within 5 days of delivery (customer pays reverse shipping). Read the Refund Policy."],
            ["What if my product arrives damaged?", "Message us with a photo/video within 48 hours of delivery. Full refund or free replacement."],
            ["Do you offer Cash on Delivery?", "Yes. COD is available at checkout across most Indian pincodes."],
        ].map(([q, a]) => (
            <div key={q} className="border-2 border-black p-6">
                <div className="font-display text-xl">{q}</div>
                <p className="mt-2 text-sm">{a}</p>
            </div>
        ))}
    </StaticPage>
);

export const Terms = () => (
    <StaticPage kicker="Legal" title="Terms & Conditions" testId="terms-page">
        <p>By using Clonzo (the website, clonzo.in) you agree to these terms. You must be 18+ or have consent of a guardian to purchase. All designs and content are © Clonzo and may not be reproduced without written permission.</p>
        <p>Prices are in INR and inclusive of GST unless stated. We reserve the right to cancel orders in cases of pricing errors, fraud, or stock issues. Full remedy is a refund of paid amount.</p>
        <p>By purchasing, you agree to our Shipping, Refund and Privacy policies.</p>
    </StaticPage>
);

export const Privacy = () => (
    <StaticPage kicker="Legal" title="Privacy Policy" testId="privacy-page">
        <p>We collect the minimum needed to fulfill your order: name, email, phone, address, and payment metadata (we do not store card details — Razorpay handles that).</p>
        <p>We use Google Analytics 4 to understand what to make more of. You can opt out via browser controls.</p>
        <p>We never sell your data. We only share order details with our print-on-demand partner (Qikink) and shipping carriers for fulfillment.</p>
        <p>Contact <a className="underline" href="mailto:hello@clonzo.in">hello@clonzo.in</a> to request data deletion or export.</p>
    </StaticPage>
);

export const Shipping = () => (
    <StaticPage kicker="Policy" title="Shipping Policy" testId="shipping-page">
        <p>Orders are printed and dispatched within 3–5 business days of confirmation. Delivery across India generally takes another 2–4 business days.</p>
        <p>Shipping is <b>free on orders above ₹999</b>. Below that, ₹49 shipping applies. COD available on most pincodes.</p>
        <p>You'll get a tracking link via email once your order ships.</p>
    </StaticPage>
);

export const Refund = () => (
    <StaticPage kicker="Policy" title="Refund, Return & Exchange" testId="refund-page">
        <div className="border-2 border-black p-6 bg-[hsl(var(--accent))] text-white">
            <div className="font-mono text-[10px] uppercase">Read first</div>
            <p className="mt-2">This is a print-on-demand brand. Every piece is made after you order it — so blanket returns don't work. Here's what we <b>can</b> do:</p>
        </div>
        <h3 className="font-display text-2xl mt-8">Size / Preference Exchange</h3>
        <p>Exchanges for size or personal preference are allowed within <b>5 days of delivery</b>. The customer/seller bears the reverse shipping cost — this is <b>not a free return</b>. Products must be unworn, unwashed, with original tags. Once we receive and inspect the item, we'll ship the replacement.</p>
        <h3 className="font-display text-2xl mt-8">Refunds (Only for defects)</h3>
        <p>Refunds are only issued for <b>damaged, defective, or incorrect items</b>, and only after the customer submits clear photo/video proof within 48 hours of delivery. Once verified, we'll issue a full refund to the original payment method within 7–10 business days.</p>
        <h3 className="font-display text-2xl mt-8">Not eligible</h3>
        <p>No refunds for "changed my mind", size guess mismatches after 5 days, or general preference-based returns. Exchange only, per above.</p>
        <p className="font-mono text-xs mt-6">Questions? Email <a className="underline" href="mailto:returns@clonzo.in">returns@clonzo.in</a> or hit us on the Contact page.</p>
    </StaticPage>
);
